/**
 * Очікування маршруту (`pendingComponent`; stability.md, розд. 1, правило 2) — тим самим механізмом,
 * що `<Area>` (`useAreaState`, `AreaTimeline`), з тими самими текстами й кодом APP-408:
 *
 * - **Поріг не подвоюється.** 300 мс до скелетона вже відчекав роутер (`defaultPendingMs`), тож
 *   поріг тут 0: скелетон — у першому кадрі компонента.
 * - **«Довше» і тайм-аут — від початку переходу**, а не від появи скелетона: 3 с і 10 с мінус
 *   `defaultPendingMs`, який роутер уже відчекав.
 * - **«Спробувати ще раз»** справді шле запити знову (`useRoutePending`, app/route-pending.ts):
 *   спершу скасовує запити в дорозі без спостерігачів — це запити `loader`-а, екран ще не
 *   змонтований, — інакше `ensureQueryData` нового `loader`-а повернув би той самий запит, що
 *   завис; далі `router.invalidate()` перериває старий `loader` і запускає новий. Відлік — заново,
 *   скелетон одразу.
 * - Мінімум показу скелетона дає роутер (`defaultPendingMinMs`), обгортки в DOM немає.
 *
 * Примітив не залежить ні від роутера, ні від клієнта запитів: стан для маршруту дає
 * `useRoutePending()` (app/route-pending.ts), тут — шкала (`usePendingSince`) і вигляд.
 */
import type { ReactNode } from 'react';
import { Area, type AreaSource, type AreaState, useAreaState } from './Area.tsx';
import type { AreaClock } from './area-timeline.ts';

export interface PendingSinceOptions {
  /** Скільки очікування вже минуло до монтування (роутер чекав `defaultPendingMs`). */
  waitedMs: number;
  /** Повтор після тайм-ауту. */
  onRetry?: () => void;
  /** Таймери; типово справжні, тест передає ручні. Значення має бути сталим. */
  clock?: AreaClock;
}

/** Стан очікування, що вже триває `waitedMs`, без роутера: поріг 0, далі фази `<Area>`. */
export function usePendingSince({ waitedMs, onRetry, clock }: PendingSinceOptions) {
  const source: AreaSource<never> = {
    data: undefined,
    status: 'pending',
    fetchStatus: 'fetching',
    ...(onRetry ? { refetch: onRetry } : {}),
  };
  return useAreaState(source, { thresholdMs: 0, waitedMs, ...(clock ? { clock } : {}) });
}

interface RoutePendingProps {
  /** Стан з `useRoutePending()` (app/route-pending.ts): викликач бере з нього ще й `aria-busy`. */
  state: AreaState<never>;
  /** Скелетон у формі екрана (`SkeletonTable`, смужки дошки, колонки картки). */
  skeleton: ReactNode;
}

/** Скелетон маршруту, з 3 с — «Довше, ніж зазвичай…», з 10 с — тайм-аут зі «Спробувати ще раз». */
export function RoutePending({ state, skeleton }: RoutePendingProps) {
  return (
    <Area state={state} skeleton={skeleton}>
      {() => null}
    </Area>
  );
}
